<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
  title: { type: String, default: '' },
  subtitle: { type: String, default: '' },
  data: {
    type: Array,
    required: true,
    // Each: { day: 0-6, hour: 0-23, count: N, avg_wait_seconds?: N }
  },
  dayLabels: {
    type: Array,
    default: () => ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
  },
  tooltipUnit: { type: String, default: 'conversas' },
  legendLess: { type: String, default: 'Menos' },
  legendMore: { type: String, default: 'Mais' },
  showAvgWait: { type: Boolean, default: false },
  avgWaitLabel: { type: String, default: 'Tempo médio na fila' },
  // GK-HUB: Show count number inside each cell
  showCount: { type: Boolean, default: false },
});

const hoveredCell = ref(null);
const tooltipStyle = ref({});
const containerRef = ref(null);

const maxCount = computed(() => {
  const max = Math.max(...props.data.map(d => d.count), 0);
  return max || 1;
});

// Group data into a 7×24 grid for easy lookup
const grid = computed(() => {
  const map = {};
  props.data.forEach(d => {
    map[`${d.day}-${d.hour}`] = {
      count: d.count,
      avgWait: d.avg_wait_seconds || 0,
    };
  });
  return map;
});

const getCell = (day, hour) =>
  grid.value[`${day}-${hour}`] || { count: 0, avgWait: 0 };
const getCount = (day, hour) => getCell(day, hour).count;

const getIntensity = (day, hour) => {
  const count = getCount(day, hour);
  if (count === 0) return 0;
  // Min 0.15, max 1.0
  return 0.15 + (count / maxCount.value) * 0.85;
};

const formatWaitTime = seconds => {
  if (!seconds || seconds <= 0) return '0s';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  if (m > 0) return s > 0 ? `${m}m ${s}s` : `${m}m`;
  return `${s}s`;
};

const hours = Array.from({ length: 24 }, (_, i) => i);

// Reorder days: Monday first (1,2,3,4,5,6,0)
const orderedDays = [1, 2, 3, 4, 5, 6, 0];

const onCellEnter = (event, day, hour) => {
  const cell = getCell(day, hour);
  hoveredCell.value = {
    day,
    hour,
    count: cell.count,
    avgWait: cell.avgWait,
  };
  const rect = event.target.getBoundingClientRect();
  const containerRect = containerRef.value?.getBoundingClientRect();
  if (containerRect) {
    tooltipStyle.value = {
      left: `${rect.left - containerRect.left + rect.width / 2}px`,
      top: `${rect.top - containerRect.top - 8}px`,
      transform: 'translate(-50%, -100%)',
    };
  }
};

const onCellLeave = () => {
  hoveredCell.value = null;
};

const formatHour = h => `${String(h).padStart(2, '0')}h`;
</script>

<template>
  <div class="mb-7">
    <div
      v-if="title"
      class="text-[13px] font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
    >
      {{ title }}
      <span v-if="subtitle" class="font-normal text-n-slate-9 text-xs">{{
        subtitle
      }}</span>
    </div>
    <div
      ref="containerRef"
      class="bg-n-solid-2 border border-n-weak rounded-[10px] p-5 relative overflow-x-auto"
    >
      <!-- Tooltip -->
      <div
        v-if="hoveredCell"
        class="absolute z-10 pointer-events-none bg-n-solid-3 border border-n-weak rounded-lg px-3 py-2 shadow-lg whitespace-nowrap"
        :style="tooltipStyle"
      >
        <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
        <div class="text-[11px] font-semibold text-n-slate-12">
          {{ dayLabels[hoveredCell.day] }}
          {{ formatHour(hoveredCell.hour) }}–{{
            formatHour(hoveredCell.hour + 1)
          }}
        </div>
        <div class="text-[11px] text-n-slate-10 mt-0.5">
          {{ hoveredCell.count }} {{ tooltipUnit }}
        </div>
        <div
          v-if="showAvgWait && hoveredCell.avgWait > 0"
          class="text-[11px] text-n-teal-9 mt-0.5"
        >
          {{ avgWaitLabel }}: {{ formatWaitTime(hoveredCell.avgWait) }}
        </div>
        <!-- eslint-enable @intlify/vue-i18n/no-raw-text -->
      </div>

      <div class="min-w-[640px]">
        <!-- Hour labels -->
        <div class="flex ml-10 mb-1">
          <div
            v-for="h in hours"
            :key="'h-' + h"
            class="flex-1 text-center text-[9px] text-n-slate-8 select-none"
          >
            <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
            {{ h % 3 === 0 ? formatHour(h) : '' }}
            <!-- eslint-enable @intlify/vue-i18n/no-raw-text -->
          </div>
        </div>

        <!-- Grid rows -->
        <div
          v-for="day in orderedDays"
          :key="'d-' + day"
          class="flex items-center mb-0.5"
        >
          <!-- Day label -->
          <div
            class="w-10 flex-shrink-0 text-[11px] text-n-slate-10 text-right pr-2 select-none"
          >
            {{ dayLabels[day] }}
          </div>
          <!-- Hour cells -->
          <div class="flex flex-1 gap-px">
            <div
              v-for="hour in hours"
              :key="'c-' + day + '-' + hour"
              class="flex-1 aspect-[1.8] rounded-[3px] cursor-default transition-all duration-150 flex items-center justify-center"
              :class="
                hoveredCell?.day === day && hoveredCell?.hour === hour
                  ? 'ring-2 ring-n-teal-9 ring-offset-1 ring-offset-n-solid-2 scale-110 z-[1]'
                  : ''
              "
              :style="{
                backgroundColor:
                  getCount(day, hour) > 0
                    ? `rgba(45, 212, 191, ${getIntensity(day, hour)})`
                    : 'var(--n-solid-3)',
              }"
              @mouseenter="e => onCellEnter(e, day, hour)"
              @mouseleave="onCellLeave"
            >
              <!-- GK-HUB: Show count inside cell -->
              <span
                v-if="showCount && getCount(day, hour) > 0"
                class="text-[8px] font-semibold leading-none select-none"
                :style="{
                  color:
                    getIntensity(day, hour) > 0.55
                      ? 'rgba(0,0,0,0.6)'
                      : 'rgba(255,255,255,0.7)',
                }"
              >
                {{ getCount(day, hour) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Legend -->
        <div class="flex items-center justify-end mt-3 gap-1.5">
          <span class="text-[10px] text-n-slate-9 mr-1">{{ legendLess }}</span>
          <div
            v-for="i in 5"
            :key="'leg-' + i"
            class="w-3 h-3 rounded-[2px]"
            :style="{
              backgroundColor:
                i === 1
                  ? 'var(--n-solid-3)'
                  : `rgba(45, 212, 191, ${0.15 + ((i - 1) / 4) * 0.85})`,
            }"
          />
          <span class="text-[10px] text-n-slate-9 ml-1">{{ legendMore }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
